import isNumber from 'lodash/isNumber';
import { WandSparkles } from 'lucide-react';
import { useRef, useState } from 'react';

import { CopyRichTextButton } from '@eluve/blocks';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  DetailsSidebarProvider,
  Divider,
  H4,
  HStack,
  Icon,
  IconNames,
  NewButton,
  P,
  VStack,
  legacyTextStyles,
  useDetailsSidebar,
  useToast,
} from '@eluve/components';
import {
  useAppointmentActivity,
  useAppointmentId,
  useAppointmentMode,
  useIsAppointmentReadonly,
  useSummary,
} from '@eluve/frontend-appointment-hooks';
import {
  AppointmentBillingCodes,
  AppointmentLanguageSelector,
  DynamicSummaryRenderer,
  MedicineRecommendations,
  RegenerateSummaryButton,
  getSummarizationErrorMessage,
} from '@eluve/frontend-feature-appointment';
import { LlmOutputFailureTypesEnum } from '@eluve/graphql-types';
import { extractSummaryMarkdownContent } from '@eluve/llm-outputs';
import { useCaptureEvent } from '@eluve/posthog-react';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';
import {
  CustomInstructions,
  FeatureFlaggedComponent,
  RollbackPreviousSummaryButton,
  SummaryFeedbackModal,
  TenantOrUserFeatureFlaggedComponent,
  customInstructionsExampleChips,
} from '@eluve/smart-blocks';

import { SummarizationErrorCard } from '../SummarizationErrorCard';

import { pendingUnsavedChangesStore } from './AppointmentLastSaved';
import { HiddenForManualCharting } from './HiddenForManualCharting';
import { DifferentialDiagnosesComponent } from './Insights/DifferentialDiagnosesComponent';

const SummaryInProgress = () => (
  <Box
    vStack
    fullWidth
    className="max-w-[500px] items-center gap-8 self-center pt-9 text-center"
  >
    <h4 className="text-2xl text-text-primary">Generating note...</h4>
    <p className="text-lg text-text-secondary">
      You can leave this page and start a new session! We&apos;ll let you know
      when your note is ready.
    </p>
  </Box>
);

const SummaryInProgressTakingLonger = () => (
  <Box
    vStack
    fullWidth
    className="max-w-[500px] items-center gap-8 self-center pt-9 text-center"
  >
    <h4 className="text-2xl text-text-primary">
      This is taking longer than expected...
    </h4>
    <p className="text-lg text-text-secondary">
      We're still working on your notes. You can leave this page and start a new
      session! We&apos;ll let you know when your note is ready.
    </p>
  </Box>
);

const SummaryFailedVariantStyles = {
  error: {
    iconName: 'AlertCircle',
    iconBg: 'bg-bg-negative-subtle',
    iconColor: 'text-text-negative-subtle',
    titleColor: 'text-text-negative-subtle',
  },
  warning: {
    iconName: 'FileExclamationPoint',
    iconBg: 'bg-bg-tertiary',
    iconColor: 'text-text-primary',
    titleColor: 'text-text-primary',
  },
};

const SummaryFailed: React.FC<{
  failureType?: LlmOutputFailureTypesEnum | null;
}> = ({ failureType }) => {
  const errorMessage = getSummarizationErrorMessage(failureType);
  const styles = SummaryFailedVariantStyles[errorMessage.variant];
  return (
    <Box
      vStack
      fullWidth
      className="max-w-[470px] items-center gap-8 self-center py-9 text-center"
    >
      <Box className={`rounded-lg p-4 ${styles.iconBg}`}>
        <Icon
          name={styles.iconName as IconNames}
          size="md"
          className={styles.iconColor}
        />
      </Box>
      <h4 className="text-2xl text-text-primary">{errorMessage.title}</h4>
      <p className="text-lg text-text-secondary">{errorMessage.description}</p>
      {errorMessage.showRegenerateHint && <RegenerateSummaryButton />}
    </Box>
  );
};

const SummaryResult = () => {
  const currentSummary = useSummary();
  const appointmentId = useAppointmentId();
  const contentRef = useRef<HTMLDivElement>(null);
  const toast = useToast();

  const isReadonly = useIsAppointmentReadonly();
  const [instructions, setInstructions] = useState<string>('');
  const tenantId = useAssignedTenantIdFromParams();

  const captureEvent = useCaptureEvent({
    tenantId,
    appointmentId,
  });

  const { setOpen } = useDetailsSidebar();

  if (!currentSummary.isSummaryAvailable) {
    return null;
  }

  return (
    <div className="flex w-full flex-col">
      {!isReadonly && (
        <VStack
          justify="between"
          wFull
          className="pb-5 lg:flex-row lg:items-center"
        >
          <HStack gap={4} className="flex-wrap">
            <HiddenForManualCharting>
              <RegenerateSummaryButton />
              <CustomInstructions
                instructions={instructions}
                setInstructions={setInstructions}
                disabled={currentSummary.isEmpty}
              />
              <RollbackPreviousSummaryButton />
            </HiddenForManualCharting>

            <CopyRichTextButton
              getContentElement={() => contentRef.current}
              markdownContent={extractSummaryMarkdownContent(
                currentSummary.summary,
              )}
              hideContentInTooltip
              size="s"
              text="Copy Summary"
              type="outline"
            />
          </HStack>

          <div>
            <TenantOrUserFeatureFlaggedComponent flag="MULTILINGUAL_SUPPORT">
              <AppointmentLanguageSelector
                inline
                outputLanguage
                regenerateSummary
              />
            </TenantOrUserFeatureFlaggedComponent>
          </div>
        </VStack>
      )}

      <SummarizationErrorCard />

      <div ref={contentRef} className="w-full">
        {currentSummary.isSummaryAvailable && (
          <DynamicSummaryRenderer
            llmOutputId={currentSummary.llmOutputId}
            humanOutputId={currentSummary.humanOutputId}
            summary={currentSummary.summary!}
            onPendingChanges={() => pendingUnsavedChangesStore.setState(true)}
            onSave={() => pendingUnsavedChangesStore.setState(false)}
            onError={() => toast.error('Failed to save block')}
          />
        )}
      </div>

      {currentSummary.isSummaryAvailable && (
        <HiddenForManualCharting>
          <Box className="flex w-full flex-row items-center justify-between gap-2 rounded-lg p-4">
            <SummaryFeedbackModal
              appointmentId={appointmentId}
              llmOutputId={currentSummary?.llmOutputId}
            />
          </Box>
        </HiddenForManualCharting>
      )}

      <Divider className="my-7 border-border-secondary" />

      <VStack gap={8} wFull className="mb-10">
        <FeatureFlaggedComponent flag="INSIGHTS">
          <VStack>
            <P
              className={legacyTextStyles.title({
                weight: 'semibold',
                size: 'l',
                className: 'text-nowrap',
              })}
            >
              Differential Diagnoses{' '}
            </P>
            <div className="w-full rounded-lg border border-border-primary bg-white p-6">
              <DifferentialDiagnosesComponent />
            </div>
          </VStack>
        </FeatureFlaggedComponent>

        <FeatureFlaggedComponent flag="BILLING_CODES">
          <AppointmentBillingCodes
            appointmentId={appointmentId}
            tenantId={tenantId}
          />
        </FeatureFlaggedComponent>

        <FeatureFlaggedComponent flag="MEDICATION">
          <MedicineRecommendations
            appointmentId={appointmentId}
            tenantId={tenantId}
          />
        </FeatureFlaggedComponent>

        {currentSummary.isSummaryAvailable &&
          !isReadonly &&
          !currentSummary.isEmpty && (
            <HiddenForManualCharting>
              <Card className="w-full rounded-xl border-border-primary">
                <CardHeader className="flex h-10 flex-row rounded-t-xl bg-bg-primary p-5">
                  <div className="flex flex-row gap-3">
                    <WandSparkles />
                    <H4>Improve your notes</H4>
                  </div>
                </CardHeader>
                <CardContent className="flex flex-col items-start rounded-b-xl bg-bg-primary px-0 py-3 pt-4">
                  {customInstructionsExampleChips?.map((chip, index) => {
                    return (
                      <div key={chip} className="w-full px-2">
                        <NewButton
                          onClick={() => {
                            captureEvent(
                              'user_clicked_top_level_improve_notes_chip',
                              {
                                instructions,
                              },
                            );
                            setInstructions(chip);
                            setOpen(true);
                          }}
                          text={chip}
                          type="ghost"
                        />
                        {index !==
                          customInstructionsExampleChips.length - 1 && (
                          <hr className="mx-3 my-1 bg-border-primary" />
                        )}
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </HiddenForManualCharting>
          )}
      </VStack>
    </div>
  );
};

export const AppointmentSummaryResult = () => {
  const {
    isSummarizationInProgress,
    latestSummaryStatus,
    latestSummaryNumAttempts,
    latestSummaryFailureType,
  } = useAppointmentActivity();
  const appointmentMode = useAppointmentMode();

  if (appointmentMode === 'MANUAL_CHARTING') {
    return (
      <DetailsSidebarProvider overlayOnly>
        <SummaryResult />
      </DetailsSidebarProvider>
    );
  }

  if (isSummarizationInProgress) {
    if (isNumber(latestSummaryNumAttempts) && latestSummaryNumAttempts < 2) {
      return <SummaryInProgress />;
    } else {
      return <SummaryInProgressTakingLonger />;
    }
  }

  if (latestSummaryStatus === 'FAILED') {
    return <SummaryFailed failureType={latestSummaryFailureType} />;
  }

  return (
    <DetailsSidebarProvider overlayOnly>
      <SummaryResult data-accessibility-label="summary-results" />
    </DetailsSidebarProvider>
  );
};
